/**
 * /forgot — identity-wide password reset request (ID.2: one credential
 * per identity, account-wide). The backend always returns a
 * success-shaped response — we never reveal whether the email exists.
 *
 * Migration note: 1:1 port of `frontend/src/app/(auth)/forgot/page.tsx`.
 * next/link → TanStack Router <Link>. No router/search-param coupling.
 */

import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi } from "@/lib/api";
import { forgotSchema, type ForgotValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";

const FIELDS = ["email"] as const;

export const Route = createFileRoute("/_auth/forgot")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const form = useForm<ForgotValues>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: ForgotValues) {
    setSubmitting(true);
    try {
      const res = await authApi.forgotPassword({ email: values.email });
      setSubmittedEmail(values.email);
      // dev_token is only returned outside production so the flow can
      // be completed without SMTP.
      setDevToken(res.dev_token ?? null);
      setSent(true);
    } catch (err) {
      handleAuthError(err, form.setError, FIELDS);
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Check your email</CardTitle>
          <CardDescription>
            If an account exists for {submittedEmail}, a reset link is on
            its way. The link expires in 1 hour.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {devToken && (
            <div className="rounded-md border border-input bg-accent/40 p-3 text-xs">
              <p className="font-medium text-accent-foreground">
                Development mode
              </p>
              <p className="mt-1 break-all text-muted-foreground">
                Reset token: <code>{devToken}</code>
              </p>
            </div>
          )}
          <Button asChild className="w-full">
            <Link
              to="/reset"
              search={{
                email: submittedEmail,
                ...(devToken ? { token: devToken } : {}),
              }}
            >
              Continue to reset password
            </Link>
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            <Link
              to="/login"
              className="font-medium text-primary hover:underline"
            >
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Reset your password</CardTitle>
        <CardDescription>
          Enter your email and we&apos;ll send a reset link. Your
          password is account-wide — resetting it updates every company
          you belong to.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Send reset link
            </Button>
          </form>
        </Form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link
            to="/login"
            className="font-medium text-primary hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}
